/**
 * El calendario de un año entero, mes a mes (14/10/2026)
 * ------------------------------------------------------
 * La rejilla de doce meses de «Vacaciones y teletrabajo» (Registro de
 * jornada, components/jornada/AusenciasTab.jsx): cada mes en su tarjeta, de
 * lunes a domingo, y cada día pintado por quien la usa (`renderDia`). También
 * la navegación de años (‹ 2099 › y «Año actual»).
 *
 * Los `data-testid` llevan el prefijo de quien la usa (`aus-calendario`,
 * `aus-mes-1`…).
 */
import React from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { INICIAL_DIA, MESES, semanasDelMes } from '../../lib/disponibilidad';
import { Button } from '../ui/button';

export const ANIO_MIN = 2000;
export const ANIO_MAX = 2200;

/**
 * Props:
 *  - anio: el año que se pinta.
 *  - prefijo: el de los data-testid (`${prefijo}-calendario`, `${prefijo}-mes-1`…).
 *  - cargando: atenúa y enseña el giro mientras llega lo nuevo.
 *  - renderDia(fecha 'aaaa-mm-dd', nombreMes): el botón (o lo que sea) de ese día.
 */
export default function RejillaAnual({ anio, prefijo, cargando = false, renderDia }) {
  return (
    <div className={`relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 ${cargando ? 'opacity-60' : ''}`}
      data-testid={`${prefijo}-calendario`} aria-busy={cargando}>
      {MESES.map((nombreMes, mes) => (
        <section key={nombreMes} className="rounded-xl border border-[#D9E3DD] bg-white p-2.5 min-w-0"
          data-testid={`${prefijo}-mes-${mes + 1}`}>
          <h2 className="text-sm font-semibold capitalize text-[#13231E] mb-1.5 px-0.5">{nombreMes}</h2>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-1">
            {INICIAL_DIA.map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {semanasDelMes(anio, mes).flat().map((f, i) => (f
              ? <React.Fragment key={f}>{renderDia(f, nombreMes)}</React.Fragment>
              : <div key={`h-${mes}-${i}`} />))}
          </div>
        </section>
      ))}
      {cargando && (
        <div className="absolute inset-0 flex items-start justify-center pt-10 pointer-events-none">
          <Loader2 className="w-5 h-5 animate-spin text-[#13231E]/60 dark:text-white/60" />
        </div>
      )}
    </div>
  );
}

/** ‹ 2099 › y, si no es el de hoy, «Año actual». */
export function NavegacionAnio({ anio, anioHoy, prefijo, onCambiar }) {
  return (
    <div className="flex items-center gap-1.5">
      <Button variant="outline" className="h-10 w-10 p-0" onClick={() => onCambiar(anio - 1)}
        disabled={anio <= ANIO_MIN} aria-label="Año anterior" data-testid={`${prefijo}-anio-anterior`}>
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="min-w-[4.5rem] text-center text-lg font-semibold tabular-nums text-[#13231E]"
        data-testid={`${prefijo}-anio`}>{anio}</div>
      <Button variant="outline" className="h-10 w-10 p-0" onClick={() => onCambiar(anio + 1)}
        disabled={anio >= ANIO_MAX} aria-label="Año siguiente" data-testid={`${prefijo}-anio-siguiente`}>
        <ChevronRight className="w-4 h-4" />
      </Button>
      {anio !== anioHoy && (
        <Button variant="ghost" className="h-10" onClick={() => onCambiar(anioHoy)}
          data-testid={`${prefijo}-anio-actual`}>
          Año actual
        </Button>
      )}
    </div>
  );
}
